import { useEffect, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { ApiError, get, post } from "@/api/client";
import AuthLayout from "@/brand/AuthLayout";

/**
 * The page a partner's admin sign-up link opens.
 *
 * Reachable signed out: the token in the link is the credential. It creates
 * the partner's Partner Admin once, then sends them to sign in — the call
 * never mints a session, so a leaked link cannot become one.
 */
export default function JoinPartnerPage() {
  const { linkId = "" } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [partner, setPartner] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    get<{ partner: string }>(`/join/${linkId}?token=${encodeURIComponent(token)}`)
      .then((r) => setPartner(r.partner))
      .catch((err) => setInvalid(err instanceof ApiError ? err.message : "This link is not valid"));
  }, [linkId, token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("The two passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const result = await post<{ ok: boolean; email: string }>(
        `/join/${linkId}`, { token, name: name.trim(), email: email.trim(), password },
      );
      setDone(result.email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  if (!token || invalid) {
    return (
      <AuthLayout>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>This link cannot be used</Typography>
          <Typography variant="body2" color="text.secondary">
            {invalid ?? "The link is incomplete. Open it exactly as it was sent."} If you
            already made your account, sign in with it.
          </Typography>
          <Button component={RouterLink} to="/login" variant="outlined">Go to sign in</Button>
        </Stack>
      </AuthLayout>
    );
  }

  if (!partner) {
    return <AuthLayout><Stack alignItems="center"><CircularProgress /></Stack></AuthLayout>;
  }

  if (done) {
    return (
      <AuthLayout>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>Your account is ready</Typography>
          <Typography variant="body2" color="text.secondary">
            {done} is now {partner}'s Partner Admin. Sign in to see your deals and invite
            your team.
          </Typography>
          <Button component={RouterLink} to="/login" variant="contained" size="large">
            Sign in
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Stack component="form" onSubmit={submit} spacing={2.5}>
        <div>
          <Typography variant="h5" fontWeight={600}>Set up {partner} on MultiDoc</Typography>
          <Typography variant="body2" color="text.secondary">
            Create your account as {partner}'s Partner Admin. You can invite your colleagues
            once you are in.
          </Typography>
        </div>

        {error && <Alert severity="error">{error}</Alert>}

        <TextField label="Your name" value={name} autoFocus autoComplete="name"
                   onChange={(e) => setName(e.target.value)} />
        <TextField label="Work email" type="email" value={email} autoComplete="email"
                   onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" type="password" value={password} autoComplete="new-password"
                   helperText="At least 12 characters, with an upper-case letter, a lower-case letter and a digit."
                   onChange={(e) => setPassword(e.target.value)} />
        <TextField label="Password again" type="password" value={confirm}
                   autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} />

        <Button type="submit" variant="contained" size="large"
                disabled={busy || !name.trim() || !email.trim() || !password}>
          {busy ? "Creating…" : "Create my account"}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Already have an account? <RouterLink to="/login">Sign in</RouterLink>
        </Typography>
      </Stack>
    </AuthLayout>
  );
}
